import styled, { css } from 'styled-components';

import { EngineFilterIcon, TransmissionFilterIcon } from '../../assets';
import { ThemeProp } from '../../theme/Themes';
import { setSVGChildColors } from '../../utils/Listings';
import { Button, SecondaryButton, ShimmerText } from '..';

export const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  box-shadow: ${(props: ThemeProp) => props.theme.shadows.primary};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 100%;
    width: 100%;
    box-shadow: none;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;

  p {
    letter-spacing: normal;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    gap: 0.5rem;
    padding: 0;
    max-height: 28rem;
    overflow: auto;
  }
`;

interface FilterItemProps {
  $isOpen?: boolean;
  $inUse?: boolean;
  $width?: string;
  $height?: string;
  $svgWidth?: string;
  $svgHeight?: string;
  $mobileSvgWidth?: string;
  $mobileSvgHeight?: string;
}

export const FilterItem = styled.div<FilterItemProps>`
  display: flex;
  position: relative;
  width: 100%;
  cursor: pointer;
  padding: 0.625rem 0.5rem;
  align-items: center;
  background: ${(props: ThemeProp & FilterItemProps) =>
    (props.$isOpen || props.$inUse) && props.theme.colors.main.c050};
  border-radius: 0.375rem;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.main.c050};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: 0.625rem 0.75rem 0.75rem 0.65rem;
  }
`;

export const FilterItemContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  cursor: pointer;
  overflow: hidden;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    gap: 0.75rem;
  }
`;

export const FilterItemIcon = styled.div<FilterItemProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${(props: FilterItemProps) => props.$width || '1.25rem'};
  height: ${(props: FilterItemProps) => props.$height || '1.25rem'};
  svg {
    width: ${(props: FilterItemProps) => props.$svgWidth || '1.25rem'};
    height: ${(props: FilterItemProps) => props.$svgHeight || '1.25rem'};
  }
  ${(props: FilterItemProps & ThemeProp) =>
    setSVGChildColors(props.$isOpen || props.$inUse ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c500)}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    width: 1.5rem;
    height: 1.5rem;
    svg {
      width: ${(props: FilterItemProps) => props.$mobileSvgWidth || '1.5rem'};
      height: ${(props: FilterItemProps) => props.$mobileSvgHeight || '1.5rem'};
    }
  }
`;

export const FilterItemTitle = styled.p<FilterItemProps>`
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  white-space: nowrap;
  color: ${(props: ThemeProp & FilterItemProps) =>
    props.$isOpen || props.$inUse ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c800};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
    line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
    letter-spacing: normal;
  }
`;

export const FilterItemSelectedValue = styled(FilterItemTitle)`
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props: ThemeProp) => props.theme.colors.main.c600};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  }
`;

export const FilterItemSubtitle = styled.p`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c600};
`;

export const FilterItemInformation = styled.div`
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  justify-content: flex-start;
  flex: 1;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
`;

export const FilterItemAction = styled.div`
  height: 1.25rem;
  display: flex;
  align-items: center;
`;

export const Divider = styled.div`
  height: 1px;
  background-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c500};
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const StyledButton = styled(Button)`
  height: 1.75rem;
  flex: 1;
  min-width: unset !important;
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xs};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 3rem;
    font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.body1.fontSize};
  }
`;

export const StyledSecondaryButton = styled(SecondaryButton)`
  height: 1.75rem;
  flex: 1;
  min-width: unset !important;
  border: 1px solid ${({ theme }: ThemeProp) => theme.colors.neutrals.c200} !important;
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xs};
  color: ${({ theme }: ThemeProp) => theme.colors.content.default};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 3rem;
    font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.body1.fontSize};
  }
`;

interface FilterIconProps {
  $active?: boolean;
}

export const StyledTransmissionFilterIcon = styled(TransmissionFilterIcon).attrs(
  (props: FilterIconProps & ThemeProp) => {
    return {
      fill: props.$active ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c500,
    };
  },
)``;

export const StyledEngineFilterIcon = styled(EngineFilterIcon).attrs((props: FilterIconProps & ThemeProp) => {
  return {
    fill: props.$active ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c500,
  };
})``;

interface StyledShimmerTitleProps {
  $borderRadius?: string;
}

export const StyledShimmerTitle = styled(ShimmerText)<StyledShimmerTitleProps>`
  && .shimmer {
    ${(props: StyledShimmerTitleProps) =>
      props.$borderRadius
        ? css`
            border-radius: ${props.$borderRadius};
          `
        : null}
  }
`;

export const ShimmerOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0.625rem 0;
  justify-content: space-between;
`;

export const ShimmerOptionsIconAndLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;
